import { useEffect, useRef } from 'react';

export interface UseKeypadInputOptions {
  onDigit: (d: string) => void;
  onDelete: () => void;
  onSubmit?: () => void;
  enabled?: boolean;
}

/**
 * Registers a global `keydown` listener that maps physical keyboard events
 * to keypad actions (digit, delete, submit).
 *
 * Uses refs to keep callbacks fresh without re-subscribing on every render.
 * The listener is removed on unmount.
 */
export function useKeypadInput({
  onDigit,
  onDelete,
  onSubmit,
  enabled = true,
}: UseKeypadInputOptions): void {
  const onDigitRef = useRef(onDigit);
  const onDeleteRef = useRef(onDelete);
  const onSubmitRef = useRef(onSubmit);
  const enabledRef = useRef(enabled);

  // Keep refs current on every render
  useEffect(() => {
    onDigitRef.current = onDigit;
  });
  useEffect(() => {
    onDeleteRef.current = onDelete;
  });
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  });
  useEffect(() => {
    enabledRef.current = enabled;
  });

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!enabledRef.current) return;
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        onDigitRef.current(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        onDeleteRef.current();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        onSubmitRef.current?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, []); // empty deps — stable subscription, refs handle freshness
}
